import { useMemo, useState } from 'react';
import ClearIcon from '@mui/icons-material/Clear';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';
import {
  Alert,
  Box,
  Button,
  Chip,
  ListSubheader,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useGetApiUsersRolesQuery } from '../../../../app/athenaApiGen';

type RoleActionSummary = {
  roleType: string | null;
  source: string | null;
  scope: string | null;
  actions: string[];
};

type RolePermissionMapping = Record<string, RoleActionSummary>;

// Section-derivation mirrors the rules in RolePermissionsList so the
// permission dropdown here groups by the same buckets a user sees when
// they open a role in the user-permissions panel. Kept inline for now
// while the redesign PR is in flight; when both PRs merge, this belongs
// in a shared module.
const SITE_NAVIGATION = 'Site Navigation';
const PLATFORM_SETTINGS = 'Platform Settings';
const PROGRAM_SETTINGS = 'Program Settings';
const APPROVALS = 'Approvals';
const OTHER = 'Other';

// Display order for the dropdown sections — matches the reading order
// in RolePermissionsList.
const SECTION_ORDER = [
  SITE_NAVIGATION,
  'Projects',
  'Deliverables',
  'Forms',
  'Files',
  'Library',
  APPROVALS,
  PLATFORM_SETTINGS,
  PROGRAM_SETTINGS,
  OTHER,
];

const deriveSection = (slug: string, rawScope?: string | null): string => {
  if (
    slug === 'view-homepage' ||
    slug === 'view-approval-queue' ||
    slug === 'view-library' ||
    slug === 'view-ims-summary' ||
    slug === 'view-financial-views'
  ) {
    return SITE_NAVIGATION;
  }
  if (
    slug === 'admin-financial-plans' ||
    slug === 'admin-sitewide-access' ||
    slug.startsWith('admin-platform-')
  ) {
    return PLATFORM_SETTINGS;
  }
  if (
    slug === 'admin-ims' ||
    slug.startsWith('admin-manage-') ||
    slug.startsWith('admin-set-')
  ) {
    return PROGRAM_SETTINGS;
  }
  if (slug === 'cancel-submission') return 'Deliverables';
  if (
    slug === 'internally-approve-project' ||
    slug === 'internally-approve-deliverable' ||
    slug === 'internally-approve-tar' ||
    slug === 'federally-approve-deliverable' ||
    slug.startsWith('tar-approval-')
  ) {
    return APPROVALS;
  }
  // Fall back to the raw backend scope, remapped to the section display
  // name used in the user-permissions panel.
  switch (rawScope) {
    case 'Project':
      return 'Projects';
    case 'Deliverable':
      return 'Deliverables';
    case 'Deliverable Forms':
      return 'Forms';
    case 'Attachment':
      return 'Files';
    case 'Directory':
      return 'Library';
    case 'Workflow':
      return APPROVALS;
    default:
      return OTHER;
  }
};

const sectionRank = (section: string) => {
  const idx = SECTION_ORDER.indexOf(section);
  return idx === -1 ? SECTION_ORDER.length : idx;
};

// Read-only JSON view of every role and its granted permissions in one
// place. Mirrors the shape that the browser-console dump script produces
// so it can be diffed against a saved snapshot from a different env, and
// so audit records stay consistent.
const AdminRolePermissionMappingPanel = () => {
  const [filter, setFilter] = useState('');
  // "" means "All roles". Any other value is a specific roleName the
  // dropdown has picked, in which case the JSON view narrows to that
  // one role's entry. The text filter still applies on top (useful for
  // seeing "does PA have this action?" — pick PA, type the action).
  const [selectedRoleName, setSelectedRoleName] = useState<string>('');
  // Same pattern for permissions: "" means "All permissions". Selecting
  // one narrows the JSON to only roles that hold that action slug — the
  // fastest way to answer "which roles have admin-sitewide-access?".
  const [selectedPermission, setSelectedPermission] = useState<string>('');
  const [copied, setCopied] = useState(false);

  const {
    data: rolesData = [],
    isFetching,
    error,
  } = useGetApiUsersRolesQuery();

  // Build the { RoleName: { roleType, source, scope, actions[] } } shape.
  // Sort roles alphabetically, then actions alphabetically inside each role
  // so the output is stable and diffable across snapshots. As a side-effect
  // we also collect the raw backend scope for each action slug — needed
  // downstream to categorize the permission dropdown by section.
  const { mapping, actionScopes } = useMemo(() => {
    const scopes = new Map<string, string | null>();
    const sorted = [...rolesData].sort((a, b) =>
      (a.roleName || '').localeCompare(b.roleName || ''),
    );
    const built = sorted.reduce<RolePermissionMapping>((acc, role) => {
      if (!role.roleName) return acc;
      const actions = (role.actions ?? [])
        .map((a: any) => {
          const name = a?.action || a?.actionName || '';
          if (name && !scopes.has(name)) {
            scopes.set(name, a?.scope ?? null);
          }
          return name;
        })
        .filter(Boolean)
        .sort((a: string, b: string) => a.localeCompare(b));
      acc[role.roleName] = {
        roleType: (role as any).roleType ?? null,
        source: (role as any).source ?? null,
        scope: (role as any).scope ?? null,
        actions,
      };
      return acc;
    }, {});
    return { mapping: built, actionScopes: scopes };
  }, [rolesData]);

  // Union of every action slug across all roles, grouped by the same
  // sections the user sees in RolePermissionsList so the dropdown reads
  // in the same buckets: Site Navigation, Projects, Deliverables, Forms,
  // Files, Library, Approvals, Platform Settings, Program Settings, Other.
  const permissionsBySection = useMemo(() => {
    const set = new Set<string>();
    Object.values(mapping).forEach((r) => r.actions.forEach((a) => set.add(a)));
    const grouped: Record<string, string[]> = {};
    set.forEach((slug) => {
      const section = deriveSection(slug, actionScopes.get(slug));
      (grouped[section] ||= []).push(slug);
    });
    return Object.entries(grouped)
      .map(([section, items]) => ({
        section,
        items: items.sort((a, b) => a.localeCompare(b)),
      }))
      .sort((a, b) => sectionRank(a.section) - sectionRank(b.section));
  }, [mapping, actionScopes]);

  // Apply filters in order: role-picker narrows to one role, permission-
  // picker narrows to roles that hold that action, then the text filter
  // matches on role name OR any of the actions' slugs. Empty filters →
  // show everything.
  const filteredMapping = useMemo<RolePermissionMapping>(() => {
    let entries = Object.entries(mapping);
    if (selectedRoleName) {
      entries = entries.filter(([roleName]) => roleName === selectedRoleName);
    }
    if (selectedPermission) {
      entries = entries.filter(([, summary]) =>
        summary.actions.includes(selectedPermission),
      );
    }
    const q = filter.trim().toLowerCase();
    if (q) {
      entries = entries.filter(([roleName, summary]) => {
        if (roleName.toLowerCase().includes(q)) return true;
        return summary.actions.some((a) => a.toLowerCase().includes(q));
      });
    }
    return Object.fromEntries(entries);
  }, [mapping, filter, selectedRoleName, selectedPermission]);

  const jsonString = useMemo(
    () => JSON.stringify(filteredMapping, null, 2),
    [filteredMapping],
  );

  const totalRoles = Object.keys(mapping).length;
  const visibleRoles = Object.keys(filteredMapping).length;
  const visibleActionRefs = Object.values(filteredMapping).reduce(
    (n, r) => n + r.actions.length,
    0,
  );

  // When a specific permission is picked, compute the list of role
  // names that hold it — useful as a fast-scan summary above the JSON
  // block ("who has admin-sitewide-access?" → the chip row answers).
  // Computed against the raw mapping (not filteredMapping) so the
  // role-picker or text-filter doesn't hide answers.
  const rolesWithSelectedPermission = useMemo(() => {
    if (!selectedPermission) return [];
    return Object.entries(mapping)
      .filter(([, summary]) => summary.actions.includes(selectedPermission))
      .map(([roleName]) => roleName);
  }, [mapping, selectedPermission]);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(jsonString);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch {
      // Clipboard API not available (usually because we're not in a
      // secure context). Fall back to a textarea + execCommand trick.
      const ta = document.createElement('textarea');
      ta.value = jsonString;
      document.body.appendChild(ta);
      ta.select();
      try {
        document.execCommand('copy');
        setCopied(true);
        window.setTimeout(() => setCopied(false), 1500);
      } finally {
        document.body.removeChild(ta);
      }
    }
  };

  const handleDownload = () => {
    const blob = new Blob([jsonString], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    const today = new Date().toISOString().slice(0, 10);
    a.download = `role-actions-${today}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (isFetching) {
    return (
      <Typography variant="body2" color="text.secondary">
        Loading role-permission mapping…
      </Typography>
    );
  }
  if (error) {
    return (
      <Alert severity="error" variant="outlined">
        Failed to load role data.
      </Alert>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
      <Typography variant="body2" color="text.secondary">
        Read-only view of every role and its granted permissions. Same shape as
        the browser-console dump script produces — save it, diff it against
        another env, or paste it into audit records. Filtering matches role
        names <em>and</em> action slugs.
      </Typography>

      <Stack
        direction={{ xs: 'column', sm: 'row' }}
        spacing={1}
        alignItems={{ xs: 'stretch', sm: 'center' }}
      >
        <TextField
          size="small"
          select
          label="Role"
          value={selectedRoleName}
          onChange={(e) => setSelectedRoleName(e.target.value)}
          sx={{ minWidth: 220 }}
        >
          <MenuItem value="">All roles</MenuItem>
          {Object.keys(mapping).map((roleName) => (
            <MenuItem key={roleName} value={roleName}>
              {roleName}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          size="small"
          select
          label="Permission"
          value={selectedPermission}
          onChange={(e) => setSelectedPermission(e.target.value)}
          sx={{ minWidth: 260 }}
        >
          <MenuItem value="">All permissions</MenuItem>
          {permissionsBySection.flatMap(({ section, items }) => [
            <ListSubheader
              key={`__subheader-${section}`}
              sx={{
                fontWeight: 700,
                letterSpacing: '0.05em',
                lineHeight: 2,
                bgcolor: 'background.paper',
              }}
            >
              {section.toUpperCase()}
            </ListSubheader>,
            ...items.map((permission) => (
              <MenuItem key={permission} value={permission}>
                {permission}
              </MenuItem>
            )),
          ])}
        </TextField>
        <TextField
          size="small"
          fullWidth
          placeholder="Filter by role name or action slug (e.g. admin-sitewide-access)"
          value={filter}
          onChange={(e) => setFilter(e.target.value)}
          sx={{ flex: 1 }}
        />
      </Stack>

      <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        useFlexGap
      >
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          flexWrap="wrap"
          useFlexGap
        >
          <Chip
            size="small"
            label={`${visibleRoles}/${totalRoles} roles shown`}
            variant="outlined"
          />
          <Chip
            size="small"
            label={`${visibleActionRefs} permissions shown`}
            variant="outlined"
          />
          {(selectedRoleName || selectedPermission || filter.trim()) && (
            <Button
              size="small"
              variant="text"
              startIcon={<ClearIcon fontSize="small" />}
              onClick={() => {
                setSelectedRoleName('');
                setSelectedPermission('');
                setFilter('');
              }}
              sx={{ textTransform: 'none' }}
            >
              Clear filters
            </Button>
          )}
        </Stack>
        <Stack direction="row" spacing={1}>
          <Tooltip title={copied ? 'Copied!' : 'Copy JSON to clipboard'}>
            <Button
              size="small"
              variant="outlined"
              startIcon={<ContentCopyIcon fontSize="small" />}
              onClick={handleCopy}
            >
              {copied ? 'Copied' : 'Copy'}
            </Button>
          </Tooltip>
          <Tooltip title="Download JSON file">
            <Button
              size="small"
              variant="outlined"
              startIcon={<DownloadIcon fontSize="small" />}
              onClick={handleDownload}
            >
              Download
            </Button>
          </Tooltip>
        </Stack>
      </Stack>

      {selectedPermission && (
        <Box
          sx={(theme) => ({
            p: 1.5,
            border: '1px solid #E0E0E0',
            borderRadius: 1,
            backgroundColor: 'rgba(31, 77, 131, 0.05)',
            ...theme.applyStyles('dark', {
              border: '1px solid #333',
              backgroundColor: 'rgba(31, 77, 131, 0.15)',
            }),
          })}
        >
          <Typography
            variant="caption"
            sx={{
              display: 'block',
              color: 'text.secondary',
              mb: 0.75,
              fontWeight: 600,
              letterSpacing: '0.03em',
            }}
          >
            {rolesWithSelectedPermission.length === 0
              ? 'No roles have '
              : `${rolesWithSelectedPermission.length} role${rolesWithSelectedPermission.length === 1 ? '' : 's'} hold `}
            <Box
              component="span"
              sx={(theme) => ({
                fontFamily: 'monospace',
                color: '#1F4D83',
                ...theme.applyStyles('dark', { color: '#8FB4E3' }),
              })}
            >
              {selectedPermission}
            </Box>
          </Typography>
          {rolesWithSelectedPermission.length > 0 && (
            <Stack direction="row" spacing={0.75} flexWrap="wrap" useFlexGap>
              {rolesWithSelectedPermission.map((roleName) => (
                <Chip
                  key={roleName}
                  size="small"
                  label={roleName}
                  // Toggle: clicking an already-selected chip clears the
                  // role filter (equivalent to picking "All roles" in the
                  // dropdown).
                  onClick={() =>
                    setSelectedRoleName((prev) =>
                      prev === roleName ? '' : roleName,
                    )
                  }
                  variant={
                    selectedRoleName === roleName ? 'filled' : 'outlined'
                  }
                  color={selectedRoleName === roleName ? 'primary' : 'default'}
                  sx={{ cursor: 'pointer' }}
                />
              ))}
            </Stack>
          )}
        </Box>
      )}

      <Box
        component="pre"
        sx={(theme) => ({
          m: 0,
          p: 2,
          maxHeight: '60vh',
          overflow: 'auto',
          fontFamily:
            'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
          fontSize: '0.8rem',
          lineHeight: 1.55,
          color: 'text.primary',
          backgroundColor: '#F8F9FA',
          border: '1px solid #E0E0E0',
          borderRadius: 1,
          whiteSpace: 'pre',
          ...theme.applyStyles('dark', {
            backgroundColor: '#1a1a1a',
            borderColor: '#333',
          }),
        })}
      >
        {jsonString || '{}'}
      </Box>
    </Box>
  );
};

export default AdminRolePermissionMappingPanel;
